import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "../../../components/paginated-selector/filters/types";
import GroupsIcon from "@mui/icons-material/Groups";
import ApartmentIcon from "@mui/icons-material/Apartment";
import {useDropdownPosition} from "../../../hooks/useDropdownPosition";
import {ButtonV2} from "../../../components/ui/ButtonsV2/ButtonV2";

type GroupTypeValue = "all" | "department" | "manual";

const options: {value: GroupTypeValue; label: string; icon: React.ReactNode}[] = [
    {value: "all",        label: "All",                 icon: null},
    {value: "department", label: "Department-linked",   icon: <ApartmentIcon fontSize="small"/>},
    {value: "manual",     label: "Manual",              icon: <GroupsIcon fontSize="small"/>},
];

function valueFromParams(filterParams: ColumnFilterProps["filterParams"]): GroupTypeValue {
    if (filterParams.departmentLinked === true)  return "department";
    if (filterParams.departmentLinked === false) return "manual";
    return "all";
}

export const ContactGroupTypeFilter: React.FC<ColumnFilterProps> = ({label, onChange, filterParams}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 200);

    const [applied, setApplied]   = useState<GroupTypeValue>(() => valueFromParams(filterParams));
    const [selected, setSelected] = useState<GroupTypeValue>(() => valueFromParams(filterParams));

    useEffect(() => {
        const v = valueFromParams(filterParams);
        setApplied(v);
        setSelected(v);
    }, [filterParams.departmentLinked]);

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) setSelected(applied);
        setIsOpen(o => !o);
    }, [isOpen, applied]);

    const handleApply = useCallback(() => {
        setApplied(selected);
        onChange({
            departmentLinked: selected === "department" ? true : selected === "manual" ? false : undefined,
        });
        setIsOpen(false);
    }, [selected, onChange]);

    const handleClear = useCallback(() => {
        setSelected("all");
        setApplied("all");
        onChange({departmentLinked: undefined});
        setIsOpen(false);
    }, [onChange]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const appliedOption = options.find(o => o.value === applied)!;

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                {applied !== "all" && (
                    <span className="text-xs text-blue-600 font-normal">({appliedOption.label})</span>
                )}
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div
                    className={`absolute ${position} mt-1 bg-white rounded shadow-lg z-50 w-[260px] flex flex-col`}
                    onClick={e => e.stopPropagation()}
                >
                    <div>
                        {options.map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setSelected(opt.value)}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 font-normal flex items-center gap-2"
                            >
                                <input type="radio" readOnly checked={selected === opt.value} className="cursor-pointer"/>
                                {opt.icon}
                                <span>{opt.label}</span>
                            </button>
                        ))}
                    </div>
                    <div className="p-3 flex gap-2">
                        <div className="flex-1">
                            <ButtonV2 label="Clear" color="gray" onClick={handleClear}/>
                        </div>
                        <div className="flex-1">
                            <ButtonV2 label="Apply" color="blue" onClick={handleApply}/>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
